import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import type { AppliedFilters } from '../models';
import { isNumericReference } from '@shared/utils/transaction-search.util';

/**
 * FilterUrlService
 * Handles encoding/decoding of filter state to/from URL query parameters
 *
 * Responsibilities:
 * - Convert AppliedFilters object to URL query params
 * - Parse URL query params back to AppliedFilters object
 * - Handle array serialization (comma-separated strings)
 * - Maintain type safety during conversions
 *
 * URL Format Examples:
 * - Single values: ?search=armand&datePreset=7_days
 * - Arrays: ?statuses=ongoing,pending&types=8,3
 * - Date range: ?dateFrom=2025-10-24&dateTo=2025-11-19
 */
@Injectable({
  providedIn: 'root',
})
export class FilterUrlService {
  /**
   * Encode AppliedFilters to URL query parameters
   *
   * @param filters - Current filter state
   * @returns URL query parameters object
   *
   * @example
   * ```typescript
   * const filters: AppliedFilters = {
   *   statuses: ['ongoing', 'pending'],
   *   search: 'armand',
   *   dateRange: { startDate: '2025-10-24', endDate: '2025-11-19' }
   * };
   *
   * const params = service.encodeFiltersToParams(filters);
   * // Result: {
   * //   statuses: 'ongoing,pending',
   * //   search: 'armand',
   * //   dateFrom: '2025-10-24',
   * //   dateTo: '2025-11-19'
   * // }
   * ```
   */
  encodeFiltersToParams(filters: AppliedFilters): Params {
    const params: Params = {};

    // Accounts filter (array → comma-separated string)
    if (filters.accountIds?.length) {
      params['accounts'] = filters.accountIds.join(',');
    }

    // Statuses filter (array → comma-separated string)
    if (filters.status?.length) {
      params['statuses'] = filters.status.join(',');
    }

    // Transaction types filter (array → comma-separated string)
    if (filters.transactionTypes?.length) {
      params['types'] = filters.transactionTypes.join(',');
    }

    // Date range filter
    if (filters.dateRange) {
      if (filters.dateRange.preset && filters.dateRange.preset !== 'custom') {
        params['datePreset'] = filters.dateRange.preset;
      }
      if (filters.dateRange.startDate) {
        params['dateFrom'] = filters.dateRange.startDate;
      }
      if (filters.dateRange.endDate) {
        params['dateTo'] = filters.dateRange.endDate;
      }
    }

    // Categories filter (array → comma-separated string)
    if (filters.categoryIds?.length) {
      params['categories'] = filters.categoryIds.join(',');
    }

    // Counterparts (clients) filter (array → comma-separated string)
    if (filters.counterpartIds?.length) {
      params['counterparts'] = filters.counterpartIds.join(',');
    }

    // Debit/Credit filter (array → comma-separated string)
    if (filters.transactionDirection?.length) {
      params['debitCredit'] = filters.transactionDirection.join(',');
    }

    // Search term
    if (filters.searchTerm?.trim()) {
      params['search'] = filters.searchTerm.trim();
      if (filters.searchMode) {
        params['search_mode'] = filters.searchMode;
      }
    }

    return params;
  }

  /**
   * Returns all known URL filter param keys set to null.
   * Spread this before encodeFiltersToParams() to ensure stale params are deleted:
   *   updateQueryParams({ ...this.getEmptyParams(), ...encodeFiltersToParams(filters) })
   */
  getEmptyParams(): Record<string, null> {
    return {
      accounts: null,
      statuses: null,
      types: null,
      datePreset: null,
      dateFrom: null,
      dateTo: null,
      categories: null,
      debitCredit: null,
      search: null,
      search_mode: null,
      counterparts: null,
    };
  }

  /**
   * Decode URL query parameters to AppliedFilters object
   *
   * @param params - URL query parameters from ActivatedRoute
   * @returns Parsed filter state
   *
   * @example
   * ```typescript
   * const params = { statuses: 'ongoing,pending', search: 'armand' };
   * const filters = service.decodeParamsToFilters(params);
   * // Result: {
   * //   statuses: ['ongoing', 'pending'],
   * //   search: 'armand'
   * // }
   * ```
   */
  decodeParamsToFilters(params: Params): AppliedFilters {
    const filters: AppliedFilters = {};

    // Accounts filter (comma-separated string → array)
    if (params['accounts']) {
      filters.accountIds = this.parseCommaSeparated(params['accounts']);
    }

    // Statuses filter (comma-separated string → array)
    if (params['statuses']) {
      filters.status = this.parseCommaSeparated(params['statuses']);
    }

    // Transaction types filter (comma-separated string → number array)
    if (params['types']) {
      filters.transactionTypes = this.parseCommaSeparated(params['types'])
        .map(Number)
        .filter((n) => !Number.isNaN(n)); // Filter out invalid numbers from URL
    }

    // Date range filter
    const hasDatePreset = params['datePreset'];
    const hasDateFrom = params['dateFrom'];
    const hasDateTo = params['dateTo'];

    if (hasDatePreset || hasDateFrom || hasDateTo) {
      filters.dateRange = {
        preset: params['datePreset'] || undefined,
        startDate: params['dateFrom'] || undefined,
        endDate: params['dateTo'] || undefined,
      };
    }

    // Categories filter (comma-separated string → array)
    if (params['categories']) {
      filters.categoryIds = this.parseCommaSeparated(params['categories']);
    }

    // Counterparts (clients) filter (comma-separated string → array)
    if (params['counterparts']) {
      filters.counterpartIds = this.parseCommaSeparated(params['counterparts']);
    }

    // Debit/Credit filter (comma-separated string → typed array)
    if (params['debitCredit']) {
      const parsed = this.parseCommaSeparated(params['debitCredit']);
      const validValues = parsed.filter(
        (v): v is 'sent' | 'received' => v === 'sent' || v === 'received',
      );
      if (validValues.length > 0) {
        filters.transactionDirection = validValues;
      }
    }

    // Search term + mode. A reference is numeric-only on the backend; a non-numeric
    // reference restored from the URL (e.g. a shared/bookmarked link) would re-trigger
    // the 500, so ignore it here. `title` / `payment_id` searches are unaffected.
    const rawSearch = params['search'];
    const explicitMode =
      params['search_mode'] === 'reference' ||
      params['search_mode'] === 'payment_id' ||
      params['search_mode'] === 'title'
        ? params['search_mode']
        : undefined;
    // Effective mode mirrors the consumer default (reference when unspecified).
    const effectiveMode = explicitMode ?? 'reference';
    if (rawSearch && (effectiveMode !== 'reference' || isNumericReference(rawSearch))) {
      filters.searchTerm = rawSearch;
      if (explicitMode) {
        filters.searchMode = explicitMode;
      }
    }

    return filters;
  }

  /**
   * Parse comma-separated string to array
   *
   * @param value - Comma-separated string or array
   * @returns Array of strings
   *
   * @example
   * ```typescript
   * parseCommaSeparated('ongoing,pending') // ['ongoing', 'pending']
   * parseCommaSeparated(['ongoing', 'pending']) // ['ongoing', 'pending']
   * parseCommaSeparated('') // []
   * ```
   */
  private parseCommaSeparated(value: string | string[]): string[] {
    if (Array.isArray(value)) {
      return value;
    }
    if (!value || value.trim() === '') {
      return [];
    }
    return value
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v.length > 0);
  }

  /**
   * Check if filters object is empty
   *
   * @param filters - Filter state to check
   * @returns True if no filters are applied
   *
   * @example
   * ```typescript
   * isEmpty({}) // true
   * isEmpty({ search: 'armand' }) // false
   * isEmpty({ statuses: [] }) // true
   * ```
   */
  isEmpty(filters: AppliedFilters): boolean {
    const keys = Object.keys(filters) as (keyof AppliedFilters)[];

    for (const key of keys) {
      const value = filters[key];

      // Check arrays
      if (Array.isArray(value) && value.length > 0) {
        return false;
      }

      // Check date range
      if (key === 'dateRange' && value) {
        const dateRange = value as AppliedFilters['dateRange'];
        if (dateRange?.preset || dateRange?.startDate || dateRange?.endDate) {
          return false;
        }
      }

      // Check search string
      if (key === 'searchTerm' && value && (value as string).trim()) {
        return false;
      }
    }

    return true;
  }
}
